import { useQuery } from '@tanstack/react-query'
import { graphqlQuery } from '@/services/graphql'

/**
 * A selectable provider type from the backend's DB-backed lookup.
 * Mirrors `ProviderTypeType` in accounts/schema.py.
 *
 * `code` is the stable string stored/sent everywhere (e.g. "nurse", "flex").
 * `name` is the translated human label — see note on localization below.
 */
export interface ProviderTypeOption {
  code: string
  name: string
  description?: string | null
  displayOrder?: number | null
  isActive?: boolean | null
  visibleChannels?: string[] | null
}

/**
 * Canonical list of selectable provider types.
 *
 * Public query — works pre-login (registration screens) using only the API key.
 * Results are already channel-scoped by the backend (X-WTD-Channel), so the list
 * can be rendered directly without inspecting `visibleChannels`.
 *
 * NOTE on localization: `name` is translated per request locale, but the backend
 * has not seeded Arabic (`name_ar`) yet, and our client does not send a locale
 * header — so `name` is effectively English today. Prefer `getProviderTypeDisplay`
 * (which uses our local i18n for known codes) for display; use `name` only as the
 * fallback label for codes we don't have a local translation for.
 */
const PROVIDER_TYPES_QUERY = `
  query ProviderTypes {
    providerTypes(isActive: true) {
      code
      name
      description
      displayOrder
      isActive
      visibleChannels
    }
  }
`

interface ProviderTypesResponse {
  providerTypes: ProviderTypeOption[] | null
}

/**
 * Fetch the channel-scoped list of active provider types, sorted for dropdowns
 * (by `displayOrder` ascending, then `code`).
 */
export function useProviderTypes(options?: { enabled?: boolean }) {
  const query = useQuery({
    queryKey: ['providerTypes'],
    queryFn: async () => {
      const response = await graphqlQuery<ProviderTypesResponse>(PROVIDER_TYPES_QUERY)
      const types = response.data?.providerTypes ?? []
      return [...types].sort((a, b) => {
        const orderA = a.displayOrder ?? Number.MAX_SAFE_INTEGER
        const orderB = b.displayOrder ?? Number.MAX_SAFE_INTEGER
        if (orderA !== orderB) return orderA - orderB
        return a.code.localeCompare(b.code)
      })
    },
    enabled: options?.enabled ?? true,
    // Reference data — refetch sparingly.
    staleTime: 1000 * 60 * 60, // 1 hour
  })

  const providerTypes = query.data ?? []

  /** Map a code to its backend `name`, if present in the list. */
  const getNameForCode = (code: string | null | undefined): string | undefined => {
    if (!code) return undefined
    return providerTypes.find((pt) => pt.code === code)?.name ?? undefined
  }

  return {
    providerTypes,
    getNameForCode,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  }
}
